// src/components/LinkedInCallback.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const LinkedInCallback = () => {
    const navigate = useNavigate();

    // Store the backend URL (use environment variable for flexibility)
    const LINKEDIN_EXCHANGE_CODE_URL = process.env.REACT_APP_LINKEDIN_FUNCTION_URL || 'https://us-central1-middgpt.cloudfunctions.net/linkedInApi/api/linkedin/exchange-code';

    useEffect(() => {
        const handleCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');
            const storedState = localStorage.getItem('linkedin_oauth_state');

            // --- 1. Verify State ---
            // Always remove state after checking to prevent reuse
            localStorage.removeItem('linkedin_oauth_state');
            if (!state || state !== storedState) {
                console.error('State mismatch or missing state - possible CSRF attack');
                // Redirect to home or an error page, maybe with a specific error message
                navigate('/?error=state_mismatch');
                return;
            }

            // --- 2. Check for Authorization Code ---
            if (!code) {
                console.error('Authorization code missing from URL');
                // Redirect back, indicating the code was missing (might happen if user denied access)
                navigate('/networking?error=auth_code_missing');
                return;
            }

            // --- 3. Exchange Code for Token ---
            try {
                console.log(`Attempting to fetch token from: ${LINKEDIN_EXCHANGE_CODE_URL}`);
                const response = await fetch(LINKEDIN_EXCHANGE_CODE_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    // Only need to send the code
                    body: JSON.stringify({ code }),
                });

                console.log("Backend response status:", response.status, response.ok); // Log status

                // --- 4. Handle Non-OK Responses ---
                if (!response.ok) {
                    let errorData = { message: `Request failed with status: ${response.status}` }; // Default error
                    try {
                        // Attempt to parse JSON error details from the backend
                        errorData = await response.json();
                        console.error(`Backend error response (${response.status}):`, errorData);
                    } catch (parseError) {
                        // If the error response wasn't JSON, log the status text
                        console.error(`Backend error response (${response.status}) was not JSON:`, response.statusText);
                        errorData = { message: response.statusText || errorData.message };
                    }
                    // Throw an error using the message from backend if available
                    throw new Error(errorData.message || `Failed to exchange code. Status: ${response.status}`);
                }

                // --- 5. Handle Successful Response ---
                const data = await response.json(); // Parse successful JSON response
                console.log("Data received from backend:", data); // Log the received data

                // Check for the accessToken specifically
                if (data && data.accessToken) {
  console.log("Access Token FOUND in backend response.");
  localStorage.setItem('isLinkedInConnected', 'true');

  // Store token in backend for the scraper
  try {
    const storeTokenResponse = await fetch('https://us-central1-middgpt.cloudfunctions.net/linkedInApi/api/linkedin/store-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn
      }),
    });

    if (!storeTokenResponse.ok) {
      console.warn("Failed to store token in backend, but proceeding with local login");
    } else {
      console.log("Successfully stored token in backend for alumni scraper");
    }
  } catch (tokenStoreError) {
    console.warn("Error storing token in backend:", tokenStoreError);
    // Continue with local login even if token storage fails
  }

  navigate('/networking');
} else {
                    // Backend responded OK but didn't include the expected token
                    console.error("Access Token NOT FOUND in backend response:", data);
                    throw new Error('Backend response missing access token.');
                }

            } catch (error) {
                // Catches errors from fetch itself (network issues) or errors thrown above
                console.error('Error during LinkedIn callback handling:', error);
                // Navigate to the networking page with a generic failure indicator
                navigate('/networking?error=connection_failed');
            }
        };

        handleCallback();

        // Clean up function (optional, not strictly needed here as it navigates away)
        // return () => {
        //   console.log("LinkedInCallback cleanup");
        // };

    // Dependencies for useEffect
    }, [navigate, LINKEDIN_EXCHANGE_CODE_URL]); // Include navigate and the URL constant

    return (
        <div className="flex items-center justify-center h-screen">
            <div className="text-center">
                {/* Simple loading indicator */}
                <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mx-auto"></div>
                <p className="mt-4 text-lg">Connecting with LinkedIn...</p>
            </div>
        </div>
    );
};

export default LinkedInCallback;